import { useIntl } from "react-intl";
import { SimpleEntityGridColumn } from "src/app/components/grid/SimpleEntityGrid";
import { SetFilterValuesFuncParams } from "ag-grid-enterprise";
import {
    infinityDateColDef,
    infinityNumberColDef,
    infinitySelectColDef,
    infinityTextColDef,
} from "src/app/utils/aggrid/infinityLoadHelper";
import moment from "moment";
import { CellFlag, CellLinks, CellTags, UserCell } from "src/app/components";

export default function useWriteGridColumns(
    getManagerValuesAsync: (params: SetFilterValuesFuncParams) => void,
    getWriterValuesAsync: (params: SetFilterValuesFuncParams) => void,
    getMoneySiteValuesAsync: (params: SetFilterValuesFuncParams) => void,
    getRdValuesAsync: (params: SetFilterValuesFuncParams) => void,
    getWritingTypeValuesAsync: (params: SetFilterValuesFuncParams) => void,
    getThematicValuesAsync: (params: SetFilterValuesFuncParams) => void,
    getLangValuesAsync: (params: SetFilterValuesFuncParams) => void
) {
    const { formatMessage } = useIntl();

    const gridColumns: Array<SimpleEntityGridColumn> = [
        {
            field: "id",
            ...infinityNumberColDef("id"),
            hide: true,
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "publishedAt",
            ...infinityDateColDef(
                formatMessage({ id: "write.grid.col.publishedAt" }),
                "publishedAt"
            ),
            cellClassRules: {
                "order-fail": ({ data }: any) =>
                    moment(data.publishedAt).unix() <= moment().subtract(1, "d").unix(),
            },
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "manager.firstName*manager.lastName",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.manager" }),
                getManagerValuesAsync
            ),
            valueGetter: ({ data }: any) =>
                data?.manager ? `${data.manager.firstName} ${data.manager.lastName}` : "",
            cellRenderer: ({ data }: any) => <UserCell user={data.manager} />,
            autoHeight: true,
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "lang",
            ...infinitySelectColDef(
                formatMessage({ id: "keywords.grid.col.lang" }),
                getLangValuesAsync
            ),
            autoHeight: true,
            filter: "agSetColumnFilter",
            valueGetter: ({ data }) => (data?.moneySite?.lang ? `${data.moneySite?.lang}` : ""),
            cellRenderer: ({ data }: any) => <CellFlag lang={data.moneySite?.lang} />,
            width: 50,
        },
        {
            field: "moneySite.domain#platform.name",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.moneySite" }),
                getMoneySiteValuesAsync
            ),
            valueGetter: ({ data }) =>
                data?.platform
                    ? data?.platform?.name
                    : data?.moneySite
                    ? `${data?.moneySite.domain} (${data?.moneySite.pbn.name})`
                    : "N/A",
            minWidth: 200,
            maxWidth: 200,
        },
        {
            field: "rd.domain",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.publishedOn" }),
                getRdValuesAsync
            ),
            minWidth: 200,
            maxWidth: 200,
            hide: true,
        },
        {
            field: "writer.firstName*writer.lastName",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.writer" }),
                getWriterValuesAsync
            ),
            valueGetter: ({ data }) =>
                data?.writer ? `${data.writer.firstName} ${data.writer.lastName}` : "",
            cellRenderer: ({ data }: any) => <UserCell user={data.writer} />,
            minWidth: 60,
            maxWidth: 60,
        },
        {
            field: "thematic.name",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.thematic" }),
                getThematicValuesAsync
            ),
            minWidth: 107,
            maxWidth: 107,
        },
        {
            field: "contentType*content.writingType",
            ...infinitySelectColDef(
                formatMessage({ id: "content.grid.col.contentType" }),
                getWritingTypeValuesAsync
            ),
            valueFormatter: ({ data }: any) =>
                `${formatMessage({
                    id: `content.grid.col.contentType.${data.contentType}`,
                })} ${formatMessage({
                    id: `content.grid.col.writingType.${data.content.writingType}`,
                })}`,
            minWidth: 100,
            maxWidth: 100,
        },
        {
            field: "wordCount",
            ...infinityNumberColDef(formatMessage({ id: "content.grid.col.words" })),
            minWidth: 65,
            maxWidth: 65,
        },
        {
            field: "keywords",
            autoHeight: true,
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.keyword" })),
            valueGetter: ({ data }: any) => data?.keywords,
            cellRenderer: ({ data }: any) => <CellTags tags={data?.keywords} />,
            width: 200,
        },
        {
            field: "links.url#links.anchor",
            autoHeight: true,
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.links" })),
            valueGetter: ({ data }: any) =>
                data?.links
                    ? data.links[1]
                        ? `${data.links[0].url} (${data.links[0].anchor})\n ${data.links[1].url} (${data.links[1].anchor})`
                        : `${data.links[0].url} (${data.links[0].anchor})`
                    : "",
            cellRenderer: ({ data }: any) => <CellLinks links={data.links} />,
            width: 260,
        },
        {
            field: "content.title",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.title" })),
        },
        {
            field: "commentForWriter",
            ...infinityTextColDef(formatMessage({ id: "content.grid.col.commentForWriter" })),
        },
        {
            field: "createdAt",
            ...infinityDateColDef(formatMessage({ id: "content.grid.col.createdAt" }), "createdAt"),
            hide: true,
            minWidth: 130,
            maxWidth: 130,
        },
        {
            field: "updatedAt",
            ...infinityDateColDef(formatMessage({ id: "content.grid.col.updatedAt" }), "updatedAt"),
            hide: true,
            maxWidth: 130,
            minWidth: 130,
        },
    ];
    return gridColumns;
}
